import ExternalIcon from '@/assets/icons/OrderDetails/external-link-fill.svg?react';
import StarLargeEmptyIcon from '@/assets/icons/StarLargeEmptyIcon.svg?react';
import StarLargeFillIcon from '@/assets/icons/StarLargeFillIcon.svg?react';
import { InputBox } from '@/components/atoms/InputBox';
import { Checkbox } from '@/components/atoms/PcCheckbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/PcTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';
import useCopy from '@/hooks/useCopyV3';
import { OrdererInfo, OrderItem } from '@/types/order-list';
import { CarrierState } from '@/types/shipping';
import { ShippingCompany } from '@/types/user-info';
import { getIsBundleItem } from '@/utils/get-is-bundle-item';
import { ColumnDef } from '@tanstack/react-table';
import { CheckIcon, ChevronDown } from 'lucide-react'; // shadcn 기본 체크아이콘
import moment from 'moment';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

const LayoutHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="pl-[12px]">{children}</div>
);

function deepEqual(a: any, b: any): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const LayoutCell = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    className?: string;
    title?: string;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void; // 추가
  }
>(({ children, className = '', title }, ref) => (
  <div
    ref={ref}
    title={title}
    className={`pl-[12px] text-gr-800 Body5M14 ${className}`}
    // onClick={onClick} // 전달
  >
    {children}
  </div>
));

LayoutCell.displayName = 'LayoutCell';
// 체크박스 컬럼
export const checkboxColumn = ({
  allChecked,
  handleCheckAll,
  checkedOrders,
  handleCheck,
}: {
  allChecked: boolean;
  handleCheckAll: (checked: boolean) => void;
  checkedOrders: OrderItem[];
  handleCheck: (order: OrderItem, checked: boolean) => void;
}): ColumnDef<OrderItem> => ({
  id: 'checkbox',
  size: 40,
  header: () => (
    <div className="flex w-full items-center justify-center">
      <Checkbox
        checked={allChecked}
        onCheckedChange={(checked) => handleCheckAll(!!checked)}
        aria-label="전체 선택"
      />
    </div>
  ),
  cell: (cell) => {
    const rows = cell.table.getRowModel().rows;
    const order = cell.row.original;

    // ✅ 선택 불가능한 주문이면 바로 null 리턴
    if (!order.marketInfo.isOrderFurtherActionEnable) {
      return null;
    }

    // ✅ orderNo별로 첫 번째 row.id 기록
    const firstRowMap = new Map<string, string>();
    rows.forEach((row) => {
      const orderNo = (row.original as any).orderNo;
      if (!firstRowMap.has(orderNo)) {
        firstRowMap.set(orderNo, row.id);
      }
    });

    const orderNo = (order as any).orderNo;
    const firstRowId = firstRowMap.get(orderNo);

    // ✅ 현재 row가 해당 orderNo의 첫 번째 row가 아니면 체크박스 숨김
    if (
      cell.row.id !== firstRowId &&
      cell.row.original.marketInfo.marketNameEnglish === 'coupang'
    ) {
      return (
        <div className="flex items-center justify-center">
          <span className="rounded-sm bg-gr-100 px-1.5 py-0.5 text-gr-600 Caption1M12">묶음</span>
        </div>
      );
    }

    // ✅ 동일 주문번호 중 첫 번째 행이라면 체크박스 표시
    const deepEqual = (a: OrderItem, b: OrderItem) => JSON.stringify(a) === JSON.stringify(b);
    const isChecked = checkedOrders.some((o) => deepEqual(o, order));

    return (
      <div className="flex w-full items-center justify-center">
        <Checkbox
          checked={isChecked}
          onCheckedChange={(checked) => handleCheck(order, !!checked)}
          aria-label="주문 선택"
        />
      </div>
    );
  },
});

// 주문번호 컬럼
export const orderNoColumn: ColumnDef<OrderItem> = {
  id: 'orderNo',
  enableSorting: false,
  header: () => <LayoutHeader>주문번호</LayoutHeader>,
  accessorKey: 'orderNo',
  minSize: 120,
  cell: (info) => {
    const value = info.getValue();
    const isEmpty = value === undefined || value === null || value === '';
    const displayValue = isEmpty ? <span className="text-left text-gr-400"></span> : String(value);

    // 복사 기능
    const { copyToClipboard } = useCopy({
      toastMessage: '주문번호가 복사됐어요',
    });

    const handleCopy = () => {
      if (!isEmpty) copyToClipboard(String(value));
    };

    // clamp, hover:underline 추가
    const content = (
      <span
        className="  cursor-pointer  whitespace-nowrap hover:underline"
        onClick={handleCopy}
        title="클릭 시 복사"
      >
        {displayValue}
      </span>
    );

    const isBundleItem = getIsBundleItem(info);
    if (isBundleItem) return null;

    return (
      <Tooltip>
        <TooltipTrigger className="flex w-full items-center">
          <LayoutCell className="flex w-full  items-center justify-start Body5M14">
            {content}
          </LayoutCell>
        </TooltipTrigger>

        <TooltipContent haveArrow className="flex flex-col">
          <div className="flex">{String(value)}</div>
          <div className="Caption3M10">*클릭 시 복사가 가능합니다</div>
        </TooltipContent>
      </Tooltip>
    );
  },
};

// 마켓명 컬럼
export const marketColumn: ColumnDef<OrderItem> = {
  id: 'market',
  accessorKey: 'marketAlias', // StoreName을 기본 마켓명으로 사용
  enableSorting: false,
  size: 160,
  header: () => <LayoutHeader>마켓</LayoutHeader>,
  cell: (info) => {
    const marketIconUrl = info.row.original.marketInfo.marketIconUrl;
    const marketAlias = info.row.original.marketAlias;
    const marketName = info.row.original.marketInfo.marketName;

    // 2. 없으면 기존 로직 사용

    const isBundleItem = getIsBundleItem(info);
    if (isBundleItem) return null;

    return (
      <LayoutCell className="flex max-w-[160px] cursor-pointer items-center gap-1 truncate whitespace-nowrap">
        {marketIconUrl && (
          <img
            src={marketIconUrl}
            alt={marketAlias || marketName}
            className="inline-block h-5 w-5 align-middle"
          />
        )}
        <span className="truncate">{marketAlias || marketName}</span>
      </LayoutCell>
    );
  },
};

export const orderStatus: ColumnDef<OrderItem> = {
  id: 'orderStauts', // 고유 id
  header: () => <LayoutHeader>주문상태</LayoutHeader>,
  accessorKey: 'orderStatus',
  size: 90, // 컬럼 너비 설정
  enableSorting: true, // 정렬 활성화

  cell: (info) => {
    const status = info.row.original.orderStatus;
    return (
      <LayoutCell className="flex max-w-[86px] flex-col whitespace-nowrap">
        <span className="text-gr-800 Body5M14">{status}</span>
      </LayoutCell>
    );
  },
};
export const orderDateColumn: ColumnDef<OrderItem> = {
  id: 'orderDate', // 고유 id
  header: () => <LayoutHeader>주문일시</LayoutHeader>,
  accessorKey: 'orderDate',
  size: 90, // 컬럼 너비 설정
  enableSorting: true, // 정렬 활성화
  sortingFn: 'datetime', // 날짜 정렬 함수
  cell: (info) => {
    const isBundleItem = getIsBundleItem(info);
    if (isBundleItem) return null;

    const orderDate = info.getValue() as string;
    const datePart = moment(orderDate).format('YY/MM/DD');
    const timePart = moment(orderDate).format('HH:mm');
    return (
      <LayoutCell className="flex max-w-[86px] flex-col whitespace-nowrap">
        <span>{datePart}</span>
        <span>{timePart}</span>
      </LayoutCell>
    );
  },
};

export const productNameColumn: ColumnDef<OrderItem> = {
  id: 'productName',
  header: () => <LayoutHeader>상품명</LayoutHeader>,
  accessorKey: 'productName',
  enableSorting: false,
  size: 220,
  cell: (info) => {
    const value = String(info.getValue());
    const textRef = useRef<HTMLSpanElement>(null);
    const [isClamped, setIsClamped] = useState(false);

    const { copyToClipboard } = useCopy({ toastMessage: '상품명이 복사됐어요' });
    const handleCopy = () => {
      if (value) copyToClipboard(value);
    };

    useLayoutEffect(() => {
      const el = textRef.current;
      if (el) setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }, [value]);

    const content = (
      <span
        ref={textRef}
        className="line-clamp-2 block cursor-pointer whitespace-normal break-words text-left hover:underline"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
        onClick={handleCopy}
        title="클릭 시 복사"
      >
        {value}
      </span>
    );

    return isClamped ? (
      <Tooltip>
        <TooltipTrigger className="h flex items-center justify-center">
          <LayoutCell className="cursor-pointer overflow-hidden text-ellipsis">
            {content}
          </LayoutCell>
        </TooltipTrigger>
        <TooltipContent haveArrow>{value}</TooltipContent>
      </Tooltip>
    ) : (
      <LayoutCell className="pl-[12px]" onClick={handleCopy}>
        {content}
      </LayoutCell>
    );
  },
};

export const productOptionColumn: ColumnDef<OrderItem> = {
  id: 'productOption',
  header: () => <LayoutHeader>옵션명</LayoutHeader>,
  enableSorting: false,
  size: 220,
  accessorKey: 'productOption',
  cell: (info) => {
    const original = info.row.original;
    const value = original.productOption;
    const isEmpty = !value || value.trim() === '';
    const textRef = useRef<HTMLSpanElement>(null);
    const [isClamped, setIsClamped] = useState(false);

    const { copyToClipboard } = useCopy({ toastMessage: '옵션명이 복사됐어요' });
    const handleCopy = () => {
      if (value) copyToClipboard(value);
    };

    useLayoutEffect(() => {
      const el = textRef.current;
      if (el) setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }, [value]);

    if (isEmpty) {
      return (
        <LayoutCell className="cursor-default truncate whitespace-nowrap">
          <span className="text-gr-400">옵션 없음</span>
        </LayoutCell>
      );
    }

    const content = (
      <span
        ref={textRef}
        className="line-clamp-2 block cursor-pointer whitespace-normal break-words hover:underline"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
        onClick={handleCopy}
        title="클릭 시 복사"
      >
        {value}
      </span>
    );

    return isClamped ? (
      <Tooltip>
        <TooltipTrigger className="h flex items-center justify-center">
          <LayoutCell className="cursor-pointer overflow-hidden text-ellipsis">
            {content}
          </LayoutCell>
        </TooltipTrigger>
        <TooltipContent haveArrow>{value}</TooltipContent>
      </Tooltip>
    ) : (
      <LayoutCell className="cursor-pointer overflow-hidden text-ellipsis" onClick={handleCopy}>
        {content}
      </LayoutCell>
    );
  },
};

export const adminPageUrl: ColumnDef<OrderItem> = {
  id: 'MarketAdminPageUrl',
  header: () => <LayoutHeader>판매자 페이지</LayoutHeader>,
  enableSorting: false,
  size: 138,

  accessorKey: 'marketAdminPageUrl',
  cell: (info) => {
    // info.row.original에서 두 값을 모두 가져옴
    const original = info.row.original;

    return (
      <LayoutCell className="max-w-[220px] cursor-pointer truncate whitespace-nowrap">
        <a
          href={original.marketInfo.sellerAdminPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-[119px] items-center gap-[6px] rounded-[8px] bg-gr-100 px-[10px] py-[6px]"
        >
          <div className="text-gr-700 Body5M14">판매자 페이지</div>
          <ExternalIcon className="h-4 w-4 fill-gr-700 text-gr-700" />
        </a>
      </LayoutCell>
    );
  },
};

// 판매금액 컬럼
export const salesAmountColumn: ColumnDef<OrderItem> = {
  id: 'salesAmount', // 고유 id
  header: () => <LayoutHeader>결제금액</LayoutHeader>,
  size: 110, // 컬럼 너비 설정
  accessorKey: 'salesAmount',

  enableSorting: true, // 정렬 활성화
  sortingFn: 'alphanumeric', // 숫자 정렬 함수
  cell: (info) => {
    const value = info.getValue();
    // 천단위 콤마
    // return <span className="whitespace-nowrap">{Number(value).toLocaleString()}원</span>;
    return (
      <LayoutCell className="whitespace-nowrap">{Number(value).toLocaleString()}원</LayoutCell>
    );
  },
};

export const quantityColumn: ColumnDef<OrderItem> = {
  id: 'quantity', // 고유 id
  size: 60, // 컬럼 너비 설정
  header: () => <LayoutHeader>수량</LayoutHeader>,
  accessorKey: 'orderCount',
  enableSorting: true, // 정렬 활성화
  sortingFn: 'alphanumeric', // 숫자 정렬 함수
  // cell: (info) => <span className="whitespace-nowrap">{String(info.getValue())}</span>,
  cell: (info) => {
    const value = info.getValue();
    return <LayoutCell className="whitespace-nowrap">{String(value)}</LayoutCell>;
  },
};

export const productNoColumn = ({ showSellerPageButton = false } = {}): ColumnDef<OrderItem> => ({
  id: 'ExternalProductInfo',
  header: () => <LayoutHeader>판매자 상품코드</LayoutHeader>,
  size: 110,
  enableSorting: false,
  accessorKey: 'externalProductInfo',
  cell: (info) => {
    const original = info.row.original;

    // scrapping type + 옵션이 true일 때만 "판매자 페이지" 버튼 노출
    if (
      showSellerPageButton &&
      !original.marketInfo.isOrderFurtherActionEnable &&
      original.marketInfo.sellerAdminPageUrl
    ) {
      return (
        <a
          href={original.marketInfo.sellerAdminPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-[119px] items-center gap-[6px] rounded-[8px] bg-gr-100 px-[10px] py-[6px]"
        >
          <div className="text-gr-700 Body5M14">판매자 페이지</div>
          <ExternalIcon className="h-4 w-4 fill-gr-700 text-gr-700" />
        </a>
      );
    }

    // 일반 주문의 경우 기존 상품코드 UI
    const value = info.row.original.externalProductInfo;
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    // 복사 기능
    const { copyToClipboard } = useCopy({
      toastMessage: '판매자 상품코드가 복사됐어요',
    });

    const handleCopy = () => {
      if (value) copyToClipboard(value);
    };

    useLayoutEffect(() => {
      const el = textRef.current;
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    }, [value]);

    // clamp, hover:underline, 클릭 복사
    const content = (
      <span
        ref={textRef}
        className="block max-w-[110px] cursor-pointer truncate whitespace-nowrap hover:underline"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
        }}
        onClick={handleCopy}
        title="클릭 시 복사"
      >
        {value || '-'}
      </span>
    );

    return isTruncated ? (
      <Tooltip>
        <TooltipTrigger className="h flex items-center justify-center">
          <LayoutCell className="max-w-[110px] cursor-pointer truncate whitespace-nowrap">
            {content}
          </LayoutCell>
        </TooltipTrigger>
        <TooltipContent haveArrow>{value}</TooltipContent>
      </Tooltip>
    ) : (
      <LayoutCell
        className="max-w-[110px] cursor-pointer truncate whitespace-nowrap"
        onClick={handleCopy}
      >
        {content}
      </LayoutCell>
    );
  },
});

type ReceiverInfo = {
  receiverName: string;
  receiverAddress: string;
  receiverZipCode: string;
  receiverPhoneNo: string;
  shippingMemo: string;
};

export const receiverColumn: ColumnDef<OrderItem> = {
  id: 'receiverName',
  size: 140,
  enableSorting: false,
  header: () => <LayoutHeader>수령자</LayoutHeader>,
  accessorKey: 'receiverInfo',
  cell: (info) => {
    const receiver = info.getValue() as ReceiverInfo;
    const name = receiver?.receiverName ?? '';
    const phone = receiver?.receiverPhoneNo ?? '';
    const value = phone ? `${name} (${phone})` : name;

    // 복사 기능
    const { copyToClipboard } = useCopy({
      toastMessage: '수령자 정보가 복사됐어요',
    });

    const handleCopy = () => {
      if (value) copyToClipboard(value);
    };

    // clamp, hover:underline, 클릭 복사
    return (
      <LayoutCell
        className="flex max-w-[138px] cursor-pointer flex-col whitespace-nowrap hover:underline"
        title="클릭 시 복사"
      >
        <div onClick={handleCopy} className="flex flex-col ">
          <span className="truncate">{name}</span>
          {phone && <span className="truncate">({phone})</span>}
        </div>
      </LayoutCell>
    );
  },
};

export const receiverAddressColumn: ColumnDef<OrderItem> = {
  id: 'receiverAddress',
  header: () => <LayoutHeader>배송지</LayoutHeader>,
  size: 240,
  enableSorting: false,
  accessorKey: 'receiverInfo',
  cell: (info) => {
    const receiver = info.getValue() as ReceiverInfo;
    const value = `${receiver?.receiverAddress}`;
    const textRef = useRef<HTMLSpanElement>(null);
    const [isClamped, setIsClamped] = useState(false);

    useLayoutEffect(() => {
      const el = textRef.current;
      if (el) {
        setIsClamped(el.scrollHeight > el.clientHeight + 1);
      }
    }, [value]);

    const { copyToClipboard } = useCopy({
      toastMessage: '주소가 복사됐어요',
    });

    const handleCopy = () => {
      if (value) copyToClipboard(value);
    };

    // hover:underline 추가!
    const content = (
      <span
        ref={textRef}
        className="line-clamp-2 block max-w-[240px] cursor-pointer overflow-hidden text-ellipsis whitespace-pre-line hover:underline"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
        onClick={handleCopy}
        title="클릭 시 복사"
      >
        {value}
      </span>
    );

    return isClamped ? (
      <Tooltip>
        <TooltipTrigger className="h flex items-center justify-center">
          <LayoutCell className="line-clamp-2 max-w-[240px] cursor-pointer overflow-hidden text-ellipsis">
            {content}
          </LayoutCell>
        </TooltipTrigger>
        <TooltipContent haveArrow>{value}</TooltipContent>
      </Tooltip>
    ) : (
      <LayoutCell
        onClick={handleCopy}
        className="line-clamp-2 max-w-[240px] cursor-pointer overflow-hidden text-ellipsis"
      >
        {content}
      </LayoutCell>
    );
  },
};

export const recevierMemo: ColumnDef<OrderItem> = {
  id: 'recevierMemo',
  header: () => <LayoutHeader>배송메모</LayoutHeader>,
  size: 240,
  enableSorting: false,
  cell: (info) => {
    const value = info.row.original.receiverInfo.shippingMemo ?? '';
    const textRef = useRef<HTMLSpanElement>(null);
    const [isClamped, setIsClamped] = useState(false);

    // 복사 기능
    const { copyToClipboard } = useCopy({
      toastMessage: '배송메모가 복사됐어요',
    });

    const handleCopy = () => {
      if (value) copyToClipboard(value);
    };

    useLayoutEffect(() => {
      const el = textRef.current;
      if (el) {
        setIsClamped(el.scrollHeight > el.clientHeight + 1);
      }
    }, [value]);

    // clamp, hover:underline, 클릭 복사
    const content = (
      <span
        ref={textRef}
        className="line-clamp-2 block max-w-[240px] cursor-pointer overflow-hidden text-ellipsis whitespace-pre-line hover:underline"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
        onClick={handleCopy}
        title="클릭 시 복사"
      >
        {value || '-'}
      </span>
    );

    return isClamped ? (
      <Tooltip>
        <TooltipTrigger className="h flex items-center justify-center">
          <LayoutCell className="line-clamp-2 max-w-[240px] cursor-pointer overflow-hidden text-ellipsis">
            {content}
          </LayoutCell>
        </TooltipTrigger>
        <TooltipContent haveArrow>{value}</TooltipContent>
      </Tooltip>
    ) : (
      <LayoutCell
        className="line-clamp-2 max-w-[240px] cursor-pointer overflow-hidden text-ellipsis"
        onClick={handleCopy}
      >
        {content}
      </LayoutCell>
    );
  },
};

export const ordererColumn: ColumnDef<OrderItem> = {
  id: 'ordererName',
  enableSorting: false,
  size: 60,
  header: () => <LayoutHeader>구매자</LayoutHeader>,
  accessorKey: 'ordererInfo',
  cell: (info) => {
    const orderer = info.getValue() as OrdererInfo;
    const name = orderer?.ordererName ?? '';

    // 복사 기능
    const { copyToClipboard } = useCopy({
      toastMessage: '구매자명이 복사됐어요',
    });

    const handleCopy = () => {
      if (name) copyToClipboard(name);
    };

    return (
      <LayoutCell
        className="flex max-w-[58px] cursor-pointer flex-col truncate whitespace-nowrap hover:underline"
        title="클릭 시 복사"
      >
        <span onClick={handleCopy} className="truncate">
          {name}
        </span>
      </LayoutCell>
    );
  },
};

export const carrierColumn = (
  shippingCompanyData: ShippingCompany[],
  handlePrefer: (code: string) => void,
  carrierStateList: CarrierState[],
  setCarrierStateList: React.Dispatch<React.SetStateAction<CarrierState[]>>,
): ColumnDef<OrderItem> => ({
  size: 138,
  id: 'carrier',
  header: () => <LayoutHeader>택배사</LayoutHeader>,
  cell: ({ row }) => {
    const order = row.original as OrderItem;

    // scrapping type인 경우 "판매자 페이지" 버튼만 보여줌
    if (!order.marketInfo.isOrderFurtherActionEnable) {
      return (
        <a
          href={order.marketInfo.sellerAdminPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-[119px] items-center gap-[6px] rounded-[8px] bg-gr-100 px-[10px] py-[6px]"
        >
          <div className="text-gr-700 Body5M14">판매자 페이지</div>
          <ExternalIcon className="h-4 w-4 fill-gr-700 text-gr-700" />
        </a>
      );
    }

    // 기존 Popover UI
    const [popOverOpen, setPopOverOpen] = useState(false);
    const [pendingPreferChanges, setPendingPreferChanges] = useState<string[]>([]);

    const selectedCarrier = carrierStateList.find((item) => deepEqual(item.target, order))?.carrier;
    const isCarrierSelected = selectedCarrier && Object.keys(selectedCarrier).length > 0;

    const handleSelectCarrier = (shippingInfo: ShippingCompany) => {
      setCarrierStateList((prev) => {
        const exists = prev.find((item) => deepEqual(item.target, order));
        if (exists) {
          return prev.map((item) =>
            deepEqual(item.target, order) ? { ...item, carrier: shippingInfo } : item,
          );
        } else {
          return [...prev, { target: order, carrier: shippingInfo, trackingNumber: '' }];
        }
      });
      updatePrefer();
      setPopOverOpen(false);
    };

    const handleStarClick = (e: React.MouseEvent | React.PointerEvent, code: string) => {
      e.preventDefault();
      e.stopPropagation();

      // 로컬 상태에만 저장
      setPendingPreferChanges((prev) => {
        if (prev.includes(code)) {
          return prev.filter((c) => c !== code);
        } else {
          return [...prev, code];
        }
      });
    };

    const updatePrefer = () => {
      if (pendingPreferChanges.length > 0) {
        // 모든 변경사항을 실제로 적용
        pendingPreferChanges.forEach((code) => {
          handlePrefer(code);
        });
        // 변경사항 초기화
        setPendingPreferChanges([]);
      }
    };

    // 팝오버 상태가 변경될 때 처리
    const handlePopoverChange = (open: boolean) => {
      // 팝오버가 닫힐 때
      if (!open && pendingPreferChanges.length > 0) updatePrefer();
      setPopOverOpen(open);
    };

    return (
      <Popover open={popOverOpen} onOpenChange={handlePopoverChange}>
        <PopoverTrigger asChild>
          <div className="flex w-[118px] items-center gap-2 rounded-[8px] border border-gr-300 bg-white px-2 py-[6px]">
            <span className={`Caption1M12 ${isCarrierSelected ? 'text-gr-800' : 'text-gr-400'}`}>
              {isCarrierSelected ? selectedCarrier.shippingCompanyName : '택배사'}
            </span>
            <ChevronDown className="ml-auto h-[18px] w-[18px] text-gr-700" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          sideOffset={4}
          className="flex max-h-[174px] w-[180px] flex-col gap-2 overflow-y-auto bg-white p-4"
          onInteractOutside={(e) => {
            const originalEvent = e?.detail?.originalEvent;
            const target = originalEvent?.target;

            if (target instanceof Element) {
              const starButton = target.closest('button[data-star]');
              if (starButton) {
                e.preventDefault();
                return;
              }
            }
          }}
        >
          {shippingCompanyData?.map((company) => {
            const isSelected = selectedCarrier?.shippingCompanyCode === company.shippingCompanyCode;
            // 원래 상태 + 로컬 변경사항 반영
            const isPreferLocal = pendingPreferChanges.includes(company.shippingCompanyCode);
            const isPrefer = isPreferLocal ? !company.isPrefer : company.isPrefer;

            return (
              <div
                key={company.shippingCompanyCode}
                className={`flex cursor-pointer items-center rounded px-2 py-1 Body5M14 hover:bg-gr-100 ${
                  isSelected ? ' text-point-500' : ''
                }`}
              >
                <button
                  data-star
                  type="button"
                  tabIndex={-1}
                  className="mr-[3px] cursor-pointer transition-opacity hover:opacity-70"
                  onPointerDown={(e) => handleStarClick(e, company.shippingCompanyCode)}
                  onClick={(e) => handleStarClick(e, company.shippingCompanyCode)}
                >
                  {isPrefer ? (
                    <StarLargeFillIcon className="text-yellow-400" />
                  ) : (
                    <StarLargeEmptyIcon className="text-gr-300" />
                  )}
                </button>
                <span className="flex-1" onClick={() => handleSelectCarrier(company)}>
                  {company.shippingCompanyName}
                </span>
                {isSelected && <CheckIcon className="ml-auto h-5 w-5 text-point-500" />}
              </div>
            );
          })}
        </PopoverContent>
      </Popover>
    );
  },
});

export const trackingNumberColumn = (
  carrierState: CarrierState[],
  setCarrierState: React.Dispatch<React.SetStateAction<CarrierState[]>>,
  errorCarrierState: CarrierState[] = [],
  setErrorCarrierState: React.Dispatch<React.SetStateAction<CarrierState[]>>,
): ColumnDef<OrderItem> => ({
  size: 146,
  id: 'trackingNumber',
  header: () => <LayoutHeader>운송장번호</LayoutHeader>,
  cell: ({ row }) => {
    const order = row.original as OrderItem;

    // scrapping type인 경우 빈값(혹은 빈 셀)만 렌더링
    if (!order.marketInfo.isOrderFurtherActionEnable) {
      return (
        <LayoutCell>
          <div />
        </LayoutCell>
      ); // 또는 return null; 또는 <LayoutCell />;
    }

    const carrierInfo = carrierState.find((item) => deepEqual(item.target, order));
    const [temp, setTemp] = useState(carrierInfo?.trackingNumber ?? '');

    // carrierState가 바뀌면 temp도 동기화
    useEffect(() => {
      setTemp(carrierInfo?.trackingNumber ?? '');
    }, [carrierInfo?.trackingNumber]);

    // errorCarrierState에서 해당 주문이 있는지 체크
    const hasError = (errorCarrierState ?? []).some((item) => deepEqual(item.target, order));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, '');
      setTemp(value);

      // 한 글자라도 입력이 바뀌면 errorCarrierState에서 해당 주문 삭제
      if (hasError) {
        setErrorCarrierState((prev) => prev.filter((item) => !deepEqual(item.target, order)));
      }
    };

    // 포커스 아웃(blur)이나 엔터 시 carrierState에 반영
    const commit = () => {
      setCarrierState((prev) => {
        const exists = prev.find((item) => deepEqual(item.target, order));
        if (exists) {
          return prev.map((item) =>
            deepEqual(item.target, order) ? { ...item, trackingNumber: temp } : item,
          );
        } else {
          if (carrierInfo?.carrier)
            return [
              ...prev,
              { target: order, carrier: carrierInfo?.carrier, trackingNumber: temp },
            ];
          else return prev;
        }
      });
    };

    return (
      <InputBox
        type="text"
        value={temp}
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit();
            (e.target as HTMLInputElement).blur();
          }
        }}
        className={`h-[30px] w-[126px] rounded-[8px] border px-2 py-[6px] text-gr-800 Caption1M12 
          ${hasError ? 'border-red-500' : ''}`}
        placeholder="운송장 번호"
        inputMode="numeric"
        pattern="[0-9]*"
        min="0"
        maxLength={20}
        autoComplete="off"
      />
    );
  },
});
